/**
 * The Pictures view (spec 026): the story's full picture list (D-18), every
 * picture it uses, filled or still a placeholder, by character (By cast) or
 * scene by scene like a trailer storyboard (By scene), plus the noted
 * pictures no scene uses ("Not used in any scene", D-15), with a side panel
 * to read a picture's brief, see where it is used and fill it. `/story/:id?view=pictures`, with `by`, `filter` and `slot` in
 * the query. One fill is one `fillPictureSlot` step through
 * `storyStore.fillPicture` (one story history entry), which places the
 * picture everywhere it is used.
 *
 * Spec 030: the view works for its `PictureOwner` (a story by default, or a
 * standalone scene at `/scene/:id?view=pictures`): a scene's view has no
 * "By cast | By scene" switch, fills and edits through the scene's own map
 * and history (saved at once), says "scene" where it names its owner, and
 * notes the stories the scene is also in.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { PictureKind, PictureSlot, SpritePictureKind, StoryApiResponse } from '../../../../Shared/src/@types';
import {
  isSlotFilled, isSlotNeeded, isSpritePictureKind, PICTURE_KIND_LABELS, pictureKindOf, pictureMapCounts, pictureSlotId, pictureSlotLabel,
  pictureSlug, slotPlaceList, slotPlaces, slotShownCount, slotsNotInUse, SPRITE_PICTURE_KINDS, usesWithoutPicture,
} from '../../../../Shared/src/pictures';
import { BACKGROUND_NAME_MAX } from '../../../../Shared/src/operations';
import { isResettable, masterSlotOf, resetTargetsOf } from '../../../../Shared/src/storyOperations';
import { apiClient } from '../../api/client';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import { useModalContext } from '../../contexts/ModalContext';
import { isEditingTarget, isModalOpen } from '../../hooks/useSceneShortcuts';
import MediaPicker from '../../modals/MediaPicker';
import { useSceneStore } from '../../stores/sceneStore';
import { BATCH_NETWORK_NOTICE, useStoryStore } from '../../stores/storyStore';
import { usePictureExtensions } from './pictureExtensions';
import { usePictureOwner, withNoun } from './pictureOwner';
import PictureThumb from './PictureThumb';
import PictureMovement, { SlotThumb, slotPlaybacks } from './PictureMovement';
import { DEFAULT_PLAYBACK } from '../../../../Shared/src/utils/spriteSheet';
import SheetImportDialog from '../sheets/SheetImportDialog';
import { playbackToArg } from '../../../../Shared/src/operations';
import type { SpriteSize, StatePlayback } from '../../../../Shared/src/@types';
import {
  castGroups, filteredGroups, firstNeededSlot, placedText, sceneFrames, sharedSlotNote, unnamedBackgroundScenes, type CastGroup,
  type PictureSceneRow,
} from './picturesModel';
import { useMediaSrcs, usePictureScenes } from './usePictureScenes';
import type { PicturesBy, PicturesFilter } from './usePicturesNav';
import styles from './Pictures.module.scss';

export const PICTURES_TEXT = {
  title: 'Pictures',
  empty: 'No pictures yet. Add a character or a background and it shows up here.',
  unusedTitle: 'Not used in any scene',
  unusedNote: 'No scene shows these pictures. A character or background with the same name and expression would use them.',
  notUsed: 'Not used',
  removeFailed: "This picture couldn't be removed. Try again.",
  pickTitle: 'Pick a slot',
  pickText: 'Choose a needed slot to read its brief, see where it is used, and fill it.',
  noBrief: 'No brief yet.',
  noBriefPanel: 'No brief yet. Ask the assistant to describe this picture.',
  copied: 'Copied',
  /** A fill that did not apply (023's operations refused it). */
  fillFailed: "This picture couldn't be placed. Try again.",
  uploadFailed: "The image couldn't be uploaded.",
  /** D-22: a needed slot's picture into the places that show none. */
  samePicture: 'Use the same picture here',
  /** D-23: scene backgrounds with no name and no picture. */
  unnamedTitle: 'Backgrounds not named',
  unnamedNote: "Name a scene's background to list it here as a picture to fill. A plain colour background can stay as it is.",
  unnamedLabel: 'Background · not named',
  unnamedPlaceholder: 'Name this place',
  unnamedAria: (scene: string) => `Name the background of ${scene}`,
  nameIt: 'Name it',
  nameFailed: "This background couldn't be named. Try again.",
  /** 027 D-33: a picture (a character: every expression) back to its initial state; the look stays. */
  reset: 'Reset',
  resetTitle: (name: string) => `Reset ${name}?`,
  resetCharacter: (subject: string, n: number) =>
    `This resets the whole character: every expression of ${subject} (${n}) goes back to a placeholder, its picture and framing are cleared and drafts still running stop. The briefs are kept, so you can generate again straight away.`,
  /** The master's second action: its own picture alone (D-33). */
  resetMasterOnly: 'Reset this picture only',
  resetCharacterAction: (subject: string) => `Reset ${subject}`,
  resetMasterAlone: (subject: string) =>
    `Or reset this picture only: the master picture alone goes back to a placeholder and drafts still running for it stop; its brief, ${subject}'s framing and the other expressions stay, and the expressions follow the new master on their next generate.`,
  /** An expression that is not the master: this picture alone (D-33). */
  resetExpression: (subject: string) =>
    `This picture alone goes back to a placeholder: it is cleared and drafts still running stop. The brief is kept, so you can generate again straight away. ${subject}'s other expressions and framing stay as they are.`,
  /** 027 D-37: a prop's master resets the whole prop (no framing). */
  resetProp: (subject: string, n: number) =>
    `This resets the whole prop: every state of ${subject} (${n}) goes back to a placeholder, its picture is cleared and drafts still running stop. The briefs are kept, so you can generate again straight away.`,
  resetPropMasterAlone: () =>
    'Or reset this picture only: the master picture alone goes back to a placeholder and drafts still running for it stop; its brief and the other states stay, and the states follow the new master on their next generate.',
  /** A prop's state that is not the master: this picture alone. */
  resetState: (subject: string) =>
    `This picture alone goes back to a placeholder: it is cleared and drafts still running stop. The brief is kept, so you can generate again straight away. ${subject}'s other states stay as they are.`,
  resetOne: 'It goes back to a placeholder: its picture is cleared and drafts still running stop. The brief is kept, so you can generate again straight away.',
  resetKeeps: "The story's look stays. The pictures stay in your library, and Undo puts everything back.",
  resetDone: (name: string, one = false) => `${name} reset. ${one ? 'The picture stays' : 'The pictures stay'} in your library.`,
  resetFailed: "This picture couldn't be reset. Try again.",
  /** D-29: the Kind switch of a sprite subject's pictures. */
  kindTitle: 'Kind',
  kindHelp: {
    character: 'A person, cut out, with expressions.',
    prop: 'An object, cut out, with states such as closed and open.',
    other: 'A whole picture with its own background, drawn as the brief describes it.',
  } as Readonly<Record<SpritePictureKind, string>>,
  kindFailed: "The kind couldn't be changed. Try again.",
} as const;

/** D-24, D-30: a character's and a prop's pictures are cut out, so they show on a checkerboard. */
const onChecker = (kind: PictureKind) => kind === 'character' || kind === 'prop';

const isBy = (v: string | null): v is PicturesBy => v === 'cast' || v === 'scene';
const isFilter = (v: string | null): v is PicturesFilter => v === 'all' || v === 'needed' || v === 'filled';

const usesText = (n: number) => `${n} ${n === 1 ? 'use' : 'uses'}`;
const hasPicture = (slot: PictureSlot) => typeof slot.mediaId === 'number' && slot.mediaId > 0;
/** D-22: a needed slot with a picture to offer for the places that show none. */
const offersSamePicture = (slot: PictureSlot) => isSlotNeeded(slot) && hasPicture(slot) && usesWithoutPicture(slot).length > 0;
const shownText = (slot: PictureSlot) => {
  const { shown, places } = slotShownCount(slot);
  return `Shown in ${shown} of ${places} ${places === 1 ? 'place' : 'places'}`;
};

export interface PicturesViewProps {
  /** A story owner's story (026); a scene owner (spec 030) needs none. */
  story?: StoryApiResponse;
}

/** Spec 030 D-10: the note a scene's view shows when the scene is also in stories. */
export const storyNoteText = (names: readonly string[]): string =>
  `This scene is also in "${names[0]}"${names.length > 1 ? ` and ${names.length - 1} more` : ''}. Inside that story, its Pictures and look apply.`;
export const STORY_NOTE_LINK = "Open the story's pictures";

const EMPTY_STORY: Pick<StoryApiResponse, 'graph' | 'storyScenes'> = { graph: { nodes: [], edges: [], startNodeId: '' }, storyScenes: [] };

type PanelStatus =
  | { kind: 'placed'; slotId: string; text: string }
  | { kind: 'error'; slotId: string; text: string }
  | { kind: 'busy'; slotId: string };

/** A playback compared by value, whatever its key order. */
const playbackKey = (p: StatePlayback | undefined) =>
  JSON.stringify(p ? Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined).sort(([a], [b]) => a.localeCompare(b))) : null);

export const PicturesView: React.FC<PicturesViewProps> = ({ story }) => {
  const [params, setParams] = useSearchParams();
  const owner = usePictureOwner();
  const isScene = owner.ref.kind === 'scene';
  // D-14: a scene has one row, so By scene reads as By cast.
  const by: PicturesBy = !isScene && isBy(params.get('by')) ? (params.get('by') as PicturesBy) : 'cast';
  const filter: PicturesFilter = isFilter(params.get('filter')) ? (params.get('filter') as PicturesFilter) : 'all';
  const selectedId = params.get('slot');

  const setQuery = useCallback((patch: Partial<Record<'by' | 'filter' | 'slot', string | null>>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', 'pictures');
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === undefined) next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  }, [setParams]);

  const storyMap = useStoryStore((s) => s.pictureMap);
  const refreshPictureMap = useStoryStore((s) => s.refreshPictureMap);
  const storyFill = useStoryStore((s) => s.fillPicture);
  const storyUndo = useStoryStore((s) => s.undo);
  const storyRedo = useStoryStore((s) => s.redo);
  const storyPending = useStoryStore((s) => s.batchPending);
  const storyApply = useStoryStore((s) => s.applyOperations);
  // The story's map and history (026), or the scene's own (spec 030 D-5).
  const map = isScene ? owner.map : storyMap;
  const fillPicture = isScene ? owner.fillPicture : storyFill;
  const undo = isScene ? owner.undo : storyUndo;
  const redo = isScene ? owner.redo : storyRedo;
  const batchPending = isScene ? false : storyPending;
  const applyOperations = isScene ? owner.apply : storyApply;
  const ext = usePictureExtensions();
  const { addModal, removeModal } = useModalContext();

  // On opening: a dirty selected scene saves first, then the map is re-read (hand edits made elsewhere show).
  // A scene's map is live (spec 030): nothing to re-read.
  useEffect(() => {
    if (isScene) return undefined;
    let cancelled = false;
    void (async () => {
      const sc = useSceneStore.getState?.();
      if (sc?.scene && sc.isDirty) await sc.saveScene();
      if (!cancelled) await refreshPictureMap?.();
    })();
    return () => { cancelled = true; };
    // On mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cmd/Ctrl+Z and Cmd/Ctrl+Shift+Z outside text fields undo and redo the story.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isEditingTarget(e.target) || isModalOpen()) return;
      const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().includes('MAC');
      const cmd = isMac ? e.metaKey : e.ctrlKey;
      if (!cmd || e.key.toLowerCase() !== 'z') return;
      e.preventDefault();
      void (e.shiftKey ? redo() : undo());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const storyScenes = usePictureScenes(isScene ? EMPTY_STORY : story ?? EMPTY_STORY, isScene ? undefined : storyMap);
  const scenes = isScene ? owner.scenes : storyScenes;

  // D-10: the stories this scene is also in, read once when the view opens.
  const [storyLinks, setStoryLinks] = useState<{ storyId: number; storyName: string }[]>([]);
  const sceneOwnerId = isScene ? owner.ref.id : 0;
  useEffect(() => {
    if (!sceneOwnerId) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiClient.getSceneStoryLinks(sceneOwnerId);
        if (cancelled || !res?.success) return;
        const seen = new Set<number>();
        const links = (res.data?.links ?? []).filter((l) => !seen.has(l.storyId) && seen.add(l.storyId))
          .map((l) => ({ storyId: l.storyId, storyName: l.storyName }));
        setStoryLinks(links);
      } catch {
        /* no note */
      }
    })();
    return () => { cancelled = true; };
  }, [sceneOwnerId]);
  const counts = pictureMapCounts(map);
  const groups = useMemo(() => castGroups(map, scenes), [map, scenes]);
  const shownGroups = useMemo(() => filteredGroups(groups, filter), [groups, filter]);
  const groupOf = useMemo(() => {
    const out = new Map<string, CastGroup>();
    for (const g of groups) for (const s of g.slots) out.set(s.id, g);
    return out;
  }, [groups]);
  const unused = useMemo(() => slotsNotInUse(map), [map]);
  const hasSlots = (map?.slots.length ?? 0) > 0;
  const selectedUnused = selectedId ? unused.find((s) => s.id === selectedId) : undefined;
  const selected = selectedId && !selectedUnused ? groupOf.get(selectedId)?.slots.find((s) => s.id === selectedId) : undefined;
  const srcFor = useMediaSrcs(map.slots.map((s) => s.mediaId ?? 0));

  // ── Filling ──────────────────────────────────────────────────────────────
  const [status, setStatus] = useState<PanelStatus | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { setCopied(false); }, [selectedId]);

  const fill = useCallback(async (slot: PictureSlot, mediaId: number, uploaded: boolean, opts?: { uses?: PictureSlot['uses'] }) => {
    const result = await fillPicture(slot.id, mediaId, opts);
    if (result.ok) {
      setStatus({ kind: 'placed', slotId: slot.id, text: placedText(result.places, uploaded) });
    } else {
      setStatus({ kind: 'error', slotId: slot.id, text: result.reason === 'network' ? BATCH_NETWORK_NOTICE : PICTURES_TEXT.fillFailed });
    }
  }, [fillPicture]);

  // A noted picture no scene uses: one `removePictureSlot` step (one story history entry).
  const removeUnused = useCallback(async (slot: PictureSlot) => {
    setStatus({ kind: 'busy', slotId: slot.id });
    const result = await applyOperations([{ name: 'removePictureSlot', args: { slot: slot.id }, createdIds: [] }]);
    if (result.ok) {
      setStatus(null);
      setParams((prev) => {
        if (prev.get('slot') !== slot.id) return prev;
        const next = new URLSearchParams(prev);
        next.delete('slot');
        return next;
      }, { replace: true });
    } else {
      setStatus({ kind: 'error', slotId: slot.id, text: result.reason === 'network' ? BATCH_NETWORK_NOTICE : PICTURES_TEXT.removeFailed });
    }
  }, [applyOperations, setParams]);

  // D-23: naming a scene's background, one `setBackground {name}` step (one story history entry).
  const [bgNames, setBgNames] = useState<Record<string, string>>({});
  const [bgStatus, setBgStatus] = useState<Record<string, 'busy' | string>>({});
  const nameBackground = useCallback(async (scene: PictureSceneRow) => {
    const name = (bgNames[scene.key] ?? '').trim();
    if (!name) return;
    setBgStatus((prev) => ({ ...prev, [scene.key]: 'busy' }));
    // A scene owner's step is the scene operation itself (spec 030), a story's names its scene.
    const args = isScene ? { name } : { scene: scene.key, name };
    const result = await applyOperations([{ name: 'setBackground', args, createdIds: [] }]);
    if (result.ok) {
      setBgStatus((prev) => { const next = { ...prev }; delete next[scene.key]; return next; });
      setBgNames((prev) => { const next = { ...prev }; delete next[scene.key]; return next; });
      setStatus(null);
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', 'pictures');
        next.set('slot', pictureSlotId('background', name, ''));
        return next;
      }, { replace: true });
    } else {
      setBgStatus((prev) => ({ ...prev, [scene.key]: result.reason === 'network' ? BATCH_NETWORK_NOTICE : PICTURES_TEXT.nameFailed }));
    }
  }, [applyOperations, bgNames, setParams, isScene]);

  // 027 D-33: Reset, after the in-app confirm; one `resetPictureSlot` step (one story history entry).
  const [resetting, setResetting] = useState<PictureSlot | null>(null);
  /** A character's or prop's master resets the whole subject (D-37); any other slot resets alone. */
  const isMasterSlot = (slot: PictureSlot) => (slot.kind === 'character' || slot.kind === 'prop') && masterSlotOf(map, slot.subject)?.id === slot.id;
  const scopeOf = (slot: PictureSlot) => (isMasterSlot(slot) ? 'character' as const : 'slot' as const);
  const resetName = (slot: PictureSlot) => (isMasterSlot(slot) ? slot.subject : pictureSlotLabel(slot));
  const confirmReset = async (scope: 'character' | 'slot') => {
    const slot = resetting;
    if (!slot) return;
    const targets = resetTargetsOf(map, slot.id, scope).map((t) => t.id);
    const result = await applyOperations([{
      name: 'resetPictureSlot', args: { slot: slot.id, ...(slot.kind === 'character' || slot.kind === 'prop' ? { scope } : {}) }, createdIds: [],
    }]);
    if (!result.ok) throw new Error(result.reason === 'network' ? BATCH_NETWORK_NOTICE : PICTURES_TEXT.resetFailed);
    ext.slotsReset?.(targets);
    setStatus({ kind: 'placed', slotId: slot.id, text: PICTURES_TEXT.resetDone(scope === 'slot' ? pictureSlotLabel(slot) : resetName(slot), scope === 'slot' || !isMasterSlot(slot)) });
  };

  // D-26 to D-28: a sprite subject's kind, one `setPictureKind` step (one history entry, no confirm).
  const [kindError, setKindError] = useState<{ slotId: string; text: string } | null>(null);
  const [kindBusy, setKindBusy] = useState(false);
  const changeKind = async (slot: PictureSlot, kind: SpritePictureKind) => {
    if (kindBusy || pictureKindOf(map, slot.subject) === kind) return;
    const key = pictureSlug(slot.subject);
    const oldIds = (map?.slots ?? []).filter((s) => isSpritePictureKind(s.kind) && pictureSlug(s.subject) === key).map((s) => s.id);
    setKindBusy(true);
    setKindError(null);
    const result = await applyOperations([{ name: 'setPictureKind', args: { subject: slot.subject, kind }, createdIds: [] }]);
    setKindBusy(false);
    if (!result.ok) {
      setKindError({ slotId: slot.id, text: result.reason === 'network' ? BATCH_NETWORK_NOTICE : PICTURES_TEXT.kindFailed });
      return;
    }
    setStatus(null);
    setQuery({ slot: pictureSlotId(kind, slot.subject, slot.variant) });
    ext.slotsReset?.(oldIds);
  };

  const select = useCallback((slotId: string | null) => {
    setQuery({ slot: slotId });
    setStatus((s) => (s && s.slotId === slotId ? s : null));
  }, [setQuery]);

  // v15 Layout: a slot selected from off-screen (`?slot=`, Fill the next one, Name it, a kind change)
  // is scrolled into view in the cards pane, once per selection, as soon as its card renders.
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedId) { scrolledFor.current = null; return; }
    if (scrolledFor.current === selectedId) return;
    const el = Array.from(scrollRef.current?.querySelectorAll<HTMLElement>('[data-slot]') ?? [])
      .find((n) => n.dataset.slot === selectedId);
    if (!el) return;
    scrolledFor.current = selectedId;
    el.scrollIntoView?.({ block: 'nearest' });
  });

  const fileRef = useRef<HTMLInputElement>(null);
  const uploadFor = useRef<PictureSlot | null>(null);
  const startUpload = (slot: PictureSlot) => {
    select(slot.id);
    uploadFor.current = slot;
    const input = fileRef.current;
    if (!input) return;
    input.value = '';
    input.click();
  };
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const slot = uploadFor.current;
    if (!file || !slot) return;
    setStatus({ kind: 'busy', slotId: slot.id });
    let mediaId: number | undefined;
    let error: string | undefined;
    try {
      const res = await apiClient.uploadMedia(file, { name: pictureSlotLabel(slot) });
      if (res.success && typeof res.data?.id === 'number') mediaId = res.data.id;
      else error = (res.success ? undefined : res.error) || PICTURES_TEXT.uploadFailed;
    } catch (err) {
      error = err instanceof Error && err.message ? err.message : PICTURES_TEXT.uploadFailed;
    }
    if (mediaId === undefined) {
      setStatus({ kind: 'error', slotId: slot.id, text: error ?? PICTURES_TEXT.uploadFailed });
      return;
    }
    await fill(slot, mediaId, true);
  };

  const openLibrary = (slot: PictureSlot) => {
    select(slot.id);
    addModal({
      title: `Choose a picture for ${pictureSlotLabel(slot)}`,
      size: 'lg',
      body: (
        <MediaPicker
          typeFilter="image"
          lockFilter
          onSelect={(mediaId: number) => {
            removeModal();
            setStatus({ kind: 'busy', slotId: slot.id });
            void fill(slot, mediaId, false);
          }}
          onCancel={() => removeModal()}
        />
      ),
    });
  };

  // D-22: the slot's picture into only the places that show none (one `fillPictureSlot` with `uses`).
  const placeSamePicture = (slot: PictureSlot) => {
    if (!hasPicture(slot)) return;
    select(slot.id);
    setStatus({ kind: 'busy', slotId: slot.id });
    void fill(slot, slot.mediaId!, false, { uses: usesWithoutPicture(slot) });
  };

  // Spec 035 D-4: an expression's playback everywhere, one `setPicturePlayback` step (one history entry, saved at once).
  // The value just set shows (controls and preview) at once, before the scenes are re-read; it gives way when they agree or the change fails.
  const [pendingPlayback, setPendingPlayback] = useState<{ slotId: string; playback: StatePlayback } | null>(null);
  const setPlayback = useCallback(async (slot: PictureSlot, playback: StatePlayback): Promise<string | null> => {
    setPendingPlayback({ slotId: slot.id, playback });
    const result = await applyOperations([{ name: 'setPicturePlayback', args: { slotId: slot.id, playback: playbackToArg(playback) }, createdIds: [] }]);
    if (result.ok) return null;
    setPendingPlayback((p) => (p?.playback === playback ? null : p));
    return 'reason' in result && result.reason === 'network' ? BATCH_NETWORK_NOTICE : (result.errors?.[0]?.message ?? "This change couldn't be applied. Try again.");
  }, [applyOperations]);
  const storedPlayback = selected?.kind === 'character' ? slotPlaybacks(selected, scenes)[0] ?? DEFAULT_PLAYBACK : undefined;
  const shownPlayback = pendingPlayback && selected && pendingPlayback.slotId === selected.id ? pendingPlayback.playback : storedPlayback;
  const storedKey = playbackKey(storedPlayback);
  useEffect(() => {
    setPendingPlayback((p) => (p && (p.slotId !== selected?.id || playbackKey(p.playback) === storedKey) ? null : p));
  }, [storedKey, selected?.id]);

  // Spec 035 D-5: one animation of a spritesheet export fills the expression.
  const importSheet = (slot: PictureSlot) => {
    select(slot.id);
    addModal({
      title: `Import a spritesheet · ${pictureSlotLabel(slot)}`,
      size: 'xl',
      theme: 'dark',
      body: (
        <SheetImportDialog
          expression={{
            subject: slot.subject,
            label: pictureSlotLabel(slot),
            onImported: async (mediaId: number, _size: SpriteSize) => {
              const result = await fillPicture(slot.id, mediaId);
              if (result.ok) {
                setStatus({ kind: 'placed', slotId: slot.id, text: placedText(result.places, true) });
                return null;
              }
              return result.reason === 'network' ? BATCH_NETWORK_NOTICE : PICTURES_TEXT.fillFailed;
            },
          }}
          onClose={() => removeModal()}
        />
      ),
    });
  };

  const copyBrief = async (slot: PictureSlot) => {
    try {
      await navigator.clipboard.writeText(`${pictureSlotLabel(slot)}: ${slot.brief}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  // ── Rendering ────────────────────────────────────────────────────────────
  const busy = batchPending || status?.kind === 'busy';

  const card = (slot: PictureSlot) => {
    const filled = isSlotFilled(slot);
    return (
      <div
        key={slot.id}
        role="button"
        tabIndex={0}
        className={`${styles.card} ${slot.id === selectedId ? styles.cardSelected : ''}`}
        onClick={() => select(slot.id)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(slot.id); } }}
        aria-pressed={slot.id === selectedId}
        data-testid="picture-card"
        data-slot={slot.id}
        data-state={filled ? 'filled' : 'needed'}
      >
        <SlotThumb className={styles.cardThumb} filled={hasPicture(slot)} src={slot.mediaId ? srcFor(slot.mediaId) : undefined} checker={onChecker(slot.kind)} placeholderKind={slot.kind} testId="picture-card-thumb" mediaId={slot.kind === 'character' ? slot.mediaId : undefined} />
        <span className={styles.cardLabel}>
          <span data-testid="picture-card-label">{pictureSlotLabel(slot)}</span>
          {isSlotNeeded(slot) && <span className={styles.needed} data-testid="picture-card-needed">NEEDED</span>}
        </span>
        <span className={styles.cardMeta} data-testid="picture-card-uses">{usesText(slotPlaces(slot, scenes).length)}</span>
        {isSlotNeeded(slot) && hasPicture(slot) && (
          <span className={styles.cardMeta} data-testid="picture-card-shown">{shownText(slot)}</span>
        )}
        <p className={styles.cardBrief} data-testid="picture-card-brief">{slot.brief || PICTURES_TEXT.noBrief}</p>
        {/* Buttons act on the card; they do not also select it twice. */}
        <span className={styles.cardActions} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {ext.slotAction?.(slot)}
          {offersSamePicture(slot) && (
            <button type="button" className={styles.button} disabled={busy} onClick={() => placeSamePicture(slot)} data-testid="picture-card-same">
              {PICTURES_TEXT.samePicture}
            </button>
          )}
          <button type="button" className={styles.button} disabled={busy} onClick={() => startUpload(slot)} data-testid="picture-card-upload">
            Upload
          </button>
          <button type="button" className={styles.button} disabled={busy} onClick={() => openLibrary(slot)} data-testid="picture-card-library">
            Library
          </button>
        </span>
      </div>
    );
  };

  const unusedCard = (slot: PictureSlot) => (
    <div
      key={slot.id}
      role="button"
      tabIndex={0}
      className={`${styles.card} ${slot.id === selectedId ? styles.cardSelected : ''}`}
      onClick={() => select(slot.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(slot.id); } }}
      aria-pressed={slot.id === selectedId}
      data-testid="picture-unused-card"
      data-slot={slot.id}
      data-state="unused"
    >
      <span className={styles.cardLabel}>
        <span data-testid="picture-card-label">{pictureSlotLabel(slot)}</span>
        <span className={styles.cardMeta} data-testid="picture-card-unused">{PICTURES_TEXT.notUsed}</span>
      </span>
      <p className={styles.cardBrief} data-testid="picture-card-brief">{slot.brief || PICTURES_TEXT.noBrief}</p>
      {slot.why && <p className={styles.cardMeta} data-testid="picture-card-why">{slot.why}</p>}
      <span className={styles.cardActions} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <button type="button" className={styles.button} disabled={busy} onClick={() => { void removeUnused(slot); }} data-testid="picture-card-remove">
          Remove
        </button>
      </span>
    </div>
  );

  const unnamedScenes = useMemo(() => unnamedBackgroundScenes(map, scenes), [map, scenes]);
  const unnamedRow = (scene: PictureSceneRow) => {
    const sceneName = scene.doc.name || scene.name;
    const value = bgNames[scene.key] ?? '';
    const rowStatus = bgStatus[scene.key];
    return (
      <div key={`unnamed-${scene.key}`} className={styles.unnamedRow} data-testid="pictures-unnamed-row" data-scene={scene.key}>
        <span className={styles.cardLabel}>
          <span data-testid="pictures-unnamed-label">{PICTURES_TEXT.unnamedLabel}</span>
          <span className={styles.cardMeta} data-testid="pictures-unnamed-scene">{sceneName}</span>
        </span>
        <form
          className={styles.unnamedForm}
          onSubmit={(e) => { e.preventDefault(); void nameBackground(scene); }}
        >
          <input
            type="text"
            value={value}
            maxLength={BACKGROUND_NAME_MAX}
            placeholder={PICTURES_TEXT.unnamedPlaceholder}
            aria-label={PICTURES_TEXT.unnamedAria(sceneName)}
            onChange={(e) => setBgNames((prev) => ({ ...prev, [scene.key]: e.target.value }))}
            data-testid="pictures-unnamed-input"
          />
          <button
            type="submit"
            className={styles.button}
            disabled={!value.trim() || rowStatus === 'busy' || batchPending}
            data-testid="pictures-unnamed-name"
          >
            {PICTURES_TEXT.nameIt}
          </button>
        </form>
        {rowStatus && rowStatus !== 'busy' && (
          <p className={styles.error} role="alert" data-testid="pictures-unnamed-error">{rowStatus}</p>
        )}
      </div>
    );
  };
  // D-23: under the All filter only, after the groups (or under the empty text).
  const unnamedGroup = (filter === 'all' || !hasSlots) && unnamedScenes.length > 0 ? (
    <section className={`${styles.group} ${styles.groupWide}`} data-testid="pictures-unnamed">
      <h3 className={styles.groupTitle}>
        <span data-testid="pictures-unnamed-title">{PICTURES_TEXT.unnamedTitle}</span>
      </h3>
      <p className={styles.note}>{PICTURES_TEXT.unnamedNote}</p>
      <div className={styles.unnamedRows}>{unnamedScenes.map(unnamedRow)}</div>
    </section>
  ) : null;
  const unnamedKeys = new Set(unnamedScenes.map((sc) => sc.key));

  // D-15: under the All filter only, last, in both arrangements.
  const unusedGroup = filter === 'all' && unused.length > 0 ? (
    <section className={`${styles.group} ${styles.groupWide}`} data-testid="pictures-unused">
      <h3 className={styles.groupTitle}>
        <span data-testid="pictures-unused-title">{PICTURES_TEXT.unusedTitle}</span>
      </h3>
      <p className={styles.note}>{PICTURES_TEXT.unusedNote}</p>
      <div className={styles.cards}>{unused.map(unusedCard)}</div>
    </section>
  ) : null;

  // The filter chips sit in the pinned header (v15 Layout), not with the groups.
  const filterChips = (
      <div className={styles.filters} role="group" aria-label="Show">
        {([['all', 'All'], ['needed', `Needed (${counts.needed})`], ['filled', 'Filled']] as const).map(([f, label]) => (
          <button
            key={f}
            type="button"
            className={`${styles.chip} ${filter === f ? styles.chipOn : ''}`}
            aria-pressed={filter === f}
            onClick={() => setQuery({ filter: f === 'all' ? null : f })}
            data-testid={`pictures-filter-${f}`}
          >
            {label}
          </button>
        ))}
      </div>
  );
  // D-31: the groups flow side by side in one wrapping row.
  const byCast = (
    <div className={styles.groups} data-testid="pictures-groups">
      {shownGroups.map((g) => (
        <section key={g.id} className={styles.group} data-testid="picture-group" data-group={g.id}>
          <h3 className={styles.groupTitle}>
            <span data-testid="picture-group-title">{g.title}</span>
            <small className={styles.groupSub} data-testid="picture-group-subtitle">{g.subtitle}</small>
          </h3>
          <div className={styles.cards}>{g.slots.map(card)}</div>
        </section>
      ))}
      {unnamedGroup}
      {unusedGroup}
    </div>
  );

  const note = useMemo(() => sharedSlotNote(map, scenes), [map, scenes]);
  const nextNeeded = firstNeededSlot(groups);
  const byScene = (
    <>
      {scenes.map((scene) => {
        const frames = sceneFrames(scene, map);
        return (
          <section key={scene.key} className={styles.sceneRow} data-testid="picture-scene-row" data-scene={scene.key}>
            <h3 className={styles.groupTitle} data-testid="picture-scene-name">{scene.doc.name || scene.name}</h3>
            <div className={styles.frames}>
              {unnamedKeys.has(scene.key) && unnamedRow(scene)}
              {frames.map(({ slot, caption }) => {
                const filled = isSlotFilled(slot);
                return (
                  <button
                    key={slot.id}
                    type="button"
                    className={`${styles.frame} ${slot.id === selectedId ? styles.cardSelected : ''}`}
                    onClick={() => select(slot.id)}
                    aria-pressed={slot.id === selectedId}
                    data-testid="picture-frame"
                    data-slot={slot.id}
                    data-state={filled ? 'filled' : 'needed'}
                  >
                    <PictureThumb className={styles.frameThumb} filled={hasPicture(slot)} src={slot.mediaId ? srcFor(slot.mediaId) : undefined} checker={onChecker(slot.kind)} placeholderKind={slot.kind} />
                    <span className={styles.cardLabel}>
                      <span data-testid="picture-frame-label">{pictureSlotLabel(slot)}</span>
                      {isSlotNeeded(slot) && <span className={styles.needed}>NEEDED</span>}
                    </span>
                    <span className={styles.frameCaption} data-testid="picture-frame-caption">{caption}</span>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
      {note && <p className={styles.note} data-testid="pictures-shared-note">{note.text}</p>}
      {nextNeeded && (
        <div>
          <button type="button" className={styles.primary} onClick={() => select(nextNeeded.id)} data-testid="pictures-fill-next">
            Fill the next one
          </button>
        </div>
      )}
      {unusedGroup}
    </>
  );

  const panelStatus = selected && status && status.slotId === selected.id ? status : null;
  const unusedStatus = selectedUnused && status && status.slotId === selectedUnused.id ? status : null;
  const panel = selectedUnused ? (
    <>
      <span className={styles.panelGroup} data-testid="pictures-panel-group">{PICTURES_TEXT.unusedTitle}</span>
      <h3 data-testid="pictures-panel-label">{pictureSlotLabel(selectedUnused)}</h3>
      <h4>Brief</h4>
      <p data-testid="pictures-panel-brief">{selectedUnused.brief || PICTURES_TEXT.noBrief}</p>
      <h4>Used in</h4>
      <ul data-testid="pictures-panel-places"><li>{PICTURES_TEXT.unusedTitle}</li></ul>
      <div className={styles.panelActions}>
        <button type="button" className={styles.button} onClick={() => { void copyBrief(selectedUnused); }} data-testid="pictures-panel-copy">
          {copied ? PICTURES_TEXT.copied : 'Copy brief for your artist'}
        </button>
        <button type="button" className={styles.button} disabled={busy} onClick={() => { void removeUnused(selectedUnused); }} data-testid="pictures-panel-remove">
          Remove
        </button>
      </div>
      {unusedStatus?.kind === 'error' && (
        <p className={styles.error} role="alert" data-testid="pictures-panel-error">{unusedStatus.text}</p>
      )}
    </>
  ) : selected ? (
    <>
      <span className={styles.panelGroup} data-testid="pictures-panel-group">{groupOf.get(selected.id)?.title}</span>
      <h3 data-testid="pictures-panel-label">{pictureSlotLabel(selected)}</h3>
      {isSpritePictureKind(selected.kind) && (() => {
        const pressed = pictureKindOf(map, selected.subject);
        return (
          <div className={styles.kind} data-testid="pictures-panel-kind">
            <h4>{PICTURES_TEXT.kindTitle}</h4>
            <div className={styles.switchRow} role="group" aria-label={PICTURES_TEXT.kindTitle}>
              {SPRITE_PICTURE_KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`${styles.chip} ${pressed === k ? styles.chipOn : ''}`}
                  aria-pressed={pressed === k}
                  data-kind={k}
                  disabled={busy || kindBusy}
                  onClick={() => { void changeKind(selected, k); }}
                  data-testid={`pictures-panel-kind-${k}`}
                >
                  {PICTURE_KIND_LABELS[k]}
                </button>
              ))}
            </div>
            <p className={styles.note} data-testid="pictures-panel-kind-help">{PICTURES_TEXT.kindHelp[pressed]}</p>
            {kindError && kindError.slotId === selected.id && (
              <p className={styles.error} role="alert" data-testid="pictures-panel-kind-error">{kindError.text}</p>
            )}
          </div>
        );
      })()}
      <SlotThumb
        key={selected.id}
        className={styles.panelThumb}
        filled={hasPicture(selected)}
        src={selected.mediaId ? srcFor(selected.mediaId) : undefined}
        checker={onChecker(selected.kind)}
        placeholderKind={selected.kind}
        testId="pictures-panel-thumb"
        mediaId={selected.kind === 'character' ? selected.mediaId : undefined}
        playback={shownPlayback}
      />
      <PictureMovement
        slot={selected}
        label={pictureSlotLabel(selected)}
        scenes={scenes}
        busy={busy}
        shown={shownPlayback}
        apply={(playback) => setPlayback(selected, playback)}
        onImport={() => importSheet(selected)}
      />
      <h4>Brief</h4>
      <p data-testid="pictures-panel-brief">{selected.brief || PICTURES_TEXT.noBriefPanel}</p>
      <h4>Used in</h4>
      <ul data-testid="pictures-panel-places">
        {slotPlaceList(selected, scenes).map((p, i) => <li key={i}>{p.shows ? p.label : `${p.label} · placeholder`}</li>)}
      </ul>
      <div className={styles.panelActions}>
        {ext.panelAction?.(selected)}
        {offersSamePicture(selected) && (
          <button type="button" className={styles.primary} disabled={busy} onClick={() => placeSamePicture(selected)} data-testid="pictures-panel-same">
            {PICTURES_TEXT.samePicture}
          </button>
        )}
        <button type="button" className={styles.primary} disabled={busy} onClick={() => startUpload(selected)} data-testid="pictures-panel-upload">
          Upload an image
        </button>
        <button type="button" className={styles.button} disabled={busy} onClick={() => openLibrary(selected)} data-testid="pictures-panel-library">
          Choose from library
        </button>
        <button type="button" className={styles.button} onClick={() => { void copyBrief(selected); }} data-testid="pictures-panel-copy">
          {copied ? PICTURES_TEXT.copied : 'Copy brief for your artist'}
        </button>
        {/* Every slot's details: the master resets its whole character, any other slot itself (027 D-33). */}
        {isResettable(map, selected.id, scopeOf(selected)) && (
          <button type="button" className={styles.danger} disabled={busy} onClick={() => setResetting(selected)} data-testid="pictures-panel-reset">
            {PICTURES_TEXT.reset}
          </button>
        )}
      </div>
      {panelStatus?.kind === 'placed' && (
        <p className={styles.status} role="status" data-testid="pictures-panel-status">
          <span>{panelStatus.text}</span>
          <button
            type="button"
            className={styles.link}
            disabled={batchPending}
            onClick={() => { setStatus(null); void undo(); }}
            data-testid="pictures-panel-undo"
          >
            Undo
          </button>
        </p>
      )}
      {panelStatus?.kind === 'error' && (
        <p className={styles.error} role="alert" data-testid="pictures-panel-error">{panelStatus.text}</p>
      )}
    </>
  ) : (
    <>
      <h3>{PICTURES_TEXT.pickTitle}</h3>
      <p>{PICTURES_TEXT.pickText}</p>
    </>
  );

  return (
    <div className={styles.view} data-testid="pictures-view">
      <div className={styles.main} data-testid="pictures-main">
        <div className={styles.mainHead} data-testid="pictures-head">
        <header className={styles.header}>
          <h2>{PICTURES_TEXT.title}</h2>
          {!hasSlots ? (
            <p className={styles.empty} data-testid="pictures-empty">{PICTURES_TEXT.empty}</p>
          ) : (
            <>
              {counts.total > 0 && (
              <>
              <p data-testid="pictures-progress-text">
                {counts.filled} of {counts.total} filled. Placeholders play in Preview with their label, so the {owner.noun} works now.
              </p>
              <div
                className={styles.progress}
                role="progressbar"
                aria-label="Pictures filled"
                aria-valuemin={0}
                aria-valuemax={counts.total}
                aria-valuenow={counts.filled}
              >
                <div className={styles.progressFill} style={{ width: `${(counts.filled / counts.total) * 100}%` }} />
              </div>
              </>
              )}
              {!isScene && (
              <div className={styles.switchRow} role="group" aria-label="Arrange">
                <button
                  type="button"
                  className={`${styles.chip} ${by === 'cast' ? styles.chipOn : ''}`}
                  aria-pressed={by === 'cast'}
                  onClick={() => setQuery({ by: null })}
                  data-testid="pictures-by-cast"
                >
                  By cast
                </button>
                <button
                  type="button"
                  className={`${styles.chip} ${by === 'scene' ? styles.chipOn : ''}`}
                  aria-pressed={by === 'scene'}
                  onClick={() => setQuery({ by: 'scene' })}
                  data-testid="pictures-by-scene"
                >
                  By scene
                </button>
              </div>
              )}
            </>
          )}
        </header>
        {hasSlots && by === 'cast' && filterChips}
        </div>
        <div className={styles.scroll} ref={scrollRef} data-testid="pictures-scroll">
        {isScene && storyLinks.length > 0 && (
          <p className={styles.note} data-testid="pictures-story-note">
            {storyNoteText(storyLinks.map((l) => l.storyName))}{' '}
            <Link to={`/story/${storyLinks[0].storyId}?view=pictures`} data-testid="pictures-story-note-link">{STORY_NOTE_LINK}</Link>
          </p>
        )}
        {!hasSlots && unnamedGroup}
        {hasSlots && ext.topRow?.(isScene ? { owner: owner.ref } : { owner: { kind: 'story', id: story?.id ?? 0 }, storyId: story?.id ?? 0 })}
        {hasSlots && (by === 'cast' ? byCast : byScene)}
        </div>
      </div>
      {hasSlots && (
        <aside className={styles.panel} aria-label="Selected picture" data-testid="pictures-panel">
          {panel}
        </aside>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => { void onFile(e); }}
        data-testid="pictures-file-input"
      />
      <ConfirmDeleteModal
        show={!!resetting}
        title={resetting ? PICTURES_TEXT.resetTitle(resetName(resetting)) : ''}
        actions={resetting && isMasterSlot(resetting) ? [
          // The master: this picture only (when it has one), or the whole character (the default).
          ...(isResettable(map, resetting.id, 'slot')
            ? [{ key: 'reset-slot', label: PICTURES_TEXT.resetMasterOnly, variant: 'outline-danger' as const, run: () => confirmReset('slot') }]
            : []),
          { key: 'reset', label: PICTURES_TEXT.resetCharacterAction(resetting.subject), variant: 'danger' as const, isDefault: true, run: () => confirmReset('character') },
        ] : [
          { key: 'reset', label: PICTURES_TEXT.reset, variant: 'danger' as const, isDefault: true, run: () => confirmReset(resetting ? scopeOf(resetting) : 'slot') },
        ]}
        onClose={() => setResetting(null)}
      >
        {resetting && (
          <p className="mb-1" data-testid="pictures-reset-text">
            {isMasterSlot(resetting)
              ? (resetting.kind === 'prop' ? PICTURES_TEXT.resetProp : PICTURES_TEXT.resetCharacter)(
                resetting.subject, resetTargetsOf(map, resetting.id).filter((t) => t.uses.length > 0).length)
              : resetting.kind === 'character' ? PICTURES_TEXT.resetExpression(resetting.subject)
                : resetting.kind === 'prop' ? PICTURES_TEXT.resetState(resetting.subject) : PICTURES_TEXT.resetOne}
          </p>
        )}
        {resetting && isMasterSlot(resetting) && isResettable(map, resetting.id, 'slot') && (
          <p className="mb-1" data-testid="pictures-reset-master-only">
            {resetting.kind === 'prop' ? PICTURES_TEXT.resetPropMasterAlone() : PICTURES_TEXT.resetMasterAlone(resetting.subject)}
          </p>
        )}
        <p className="mb-0">{withNoun(PICTURES_TEXT.resetKeeps, owner.noun)}</p>
      </ConfirmDeleteModal>
    </div>
  );
};

export default PicturesView;
